import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Globe } from 'lucide-react'
import { useI18n } from '../i18n'
import { useUserStore } from '../stores'
import LanguageSelector from '../components/LanguageSelector'
import { APP_VERSION } from '../utils/appVersion'
import { ResponsiveWrapper } from '../components/layout'
import orellaWordmarkBlue from '../assets/logos/orella-wordmark-blue-transparent.png'
import handIcon from '../assets/hand_icon.webp'
import useSsoSignIn from '../hooks/useSsoSignIn'
import SsoCodeSheet from '../components/SsoCodeSheet'
import { AppleLogo, GoogleLogo } from '../components/SsoLogos'

/**
 * WelcomeScreen — merged splash + welcome opening sequence (v227.1.2).
 *
 * Previously this was two separate routed screens (SplashScreen → WelcomeScreen).
 * The hand-off faded the whole stage to opacity 0, then remounted a fresh screen
 * with a forward slide + a re-animating logo — which read as a flicker / white gap.
 *
 * Now it is a single persistent blue stage that never unmounts. The wordmark
 * fades in and pops up from the bottom into its home spot, holds a beat, then the
 * bottom block (hello + buttons + version) reveals in its original position.
 * Returning (authenticated) users still go straight to /home — the splash's
 * decision role is preserved, and the stage stays fully opaque the whole time,
 * so there is nothing to flicker.
 */
export default function WelcomeScreen() {
  const navigate = useNavigate()
  const { t, currentLanguage } = useI18n()
  const isAuthenticated = useUserStore((state) => state.isAuthenticated)
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const sso = useSsoSignIn({ mode: 'signup' })

  const [entered, setEntered] = useState(false)   // logo fades in + pops up
  const [opened, setOpened] = useState(false)      // bottom block reveals

  useEffect(() => {
    const tEnter = setTimeout(() => setEntered(true), 200)

    if (isAuthenticated) {
      // Returning users go straight home once the pop has played its beat.
      const tHome = setTimeout(() => navigate('/home', { replace: true }), 1400)
      return () => { clearTimeout(tEnter); clearTimeout(tHome) }
    }

    // New / logged-out users: after the pop settles, the screen opens.
    const tOpen = setTimeout(() => setOpened(true), 1650)
    return () => { clearTimeout(tEnter); clearTimeout(tOpen) }
  }, [isAuthenticated, navigate])

  return (
    <ResponsiveWrapper className="shadow-xl" safeArea={false} bottomInset={false}>
      <div className="h-[100dvh] flex flex-col relative overflow-hidden" style={{ background: '#EAF3FB' }}>
        {/* Language Selector Button - Top Right */}
        <div className="absolute top-4 right-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', zIndex: 2 }}>
          <button
            onClick={() => setShowLanguageModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm active:scale-[0.97] transition-all"
            style={{
              border: '1.5px solid rgba(167, 217, 252, 0.6)',
              opacity: opened ? 1 : 0,
              transition: 'opacity 0.5s ease 0.2s',
            }}
          >
            <Globe size={16} className="text-orella-500" />
            <span className="text-[15px] font-bold text-orella-700">{currentLanguage.nativeName}</span>
          </button>
        </div>

        {/* s95 sign-up rebuild: one calm front door. The wordmark pops into place
            (the splash beat is kept), then the line on what Orella is for and the
            three ways in. Returning users still go straight home. */}
        <div className="flex-1 flex flex-col justify-center" style={{ padding: '0 30px', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          {/* s95b: the hand wave is back beside the wordmark — Orella says hello. */}
          <div
            className="flex items-center"
            style={{
              gap: 14, marginBottom: 34,
              opacity: entered ? 1 : 0,
              transform: entered ? 'translateY(0) scale(1)' : 'translateY(46vh) scale(0.95)',
              transition: 'opacity 0.8s ease, transform 1.1s cubic-bezier(0.34, 1.4, 0.5, 1)',
              willChange: 'transform, opacity',
            }}
          >
            <img src={orellaWordmarkBlue} alt="Orella" style={{ width: 170, height: 'auto' }} />
            <img
              src={handIcon}
              alt=""
              className="wave-hand"
              style={{ width: 46, height: 46, objectFit: 'contain', opacity: opened ? 1 : 0, transition: 'opacity 0.5s ease 0.15s', animationDelay: '1.1s' }}
            />
          </div>
          <div
            style={{
              opacity: opened ? 1 : 0,
              transform: opened ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)',
            }}
          >
            <h1 style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em', color: '#15354F', margin: 0 }}>
              {t('welcome.headline', "Get more from every doctor's visit")}
            </h1>
            <p style={{ marginTop: 12, fontSize: 16.5, lineHeight: 1.55, color: '#4A5A6B' }}>
              {t('welcome.subline', 'Prepare your case before you go. Leave with a clear diagnosis and a plan.')}
            </p>
          </div>
        </div>

        <div
          className="flex flex-col gap-2.5"
          style={{
            padding: '0 22px',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
            opacity: opened ? 1 : 0,
            transform: opened ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.55s ease 0.14s, transform 0.55s cubic-bezier(0.22, 0.61, 0.36, 1) 0.14s',
          }}
        >
          {sso.ssoError && (
            <p role="alert" className="text-center" style={{ fontSize: 13, color: '#DC2626' }}>{sso.ssoError}</p>
          )}
          <button
            onClick={sso.startApple}
            disabled={!!sso.ssoLoading}
            className="w-full flex items-center justify-center gap-2.5 font-semibold rounded-2xl active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{ height: 54, fontSize: 16, background: '#000', color: '#fff', border: 'none' }}
          >
            <AppleLogo /> {sso.ssoLoading === 'apple' ? t('signUp.signingUp') : t('signUp.continueWithApple')}
          </button>
          <button
            onClick={sso.startGoogle}
            disabled={!!sso.ssoLoading}
            className="w-full flex items-center justify-center gap-2.5 font-semibold rounded-2xl active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{ height: 54, fontSize: 16, background: '#fff', color: '#15354F', border: '1.5px solid #E5EDF3' }}
          >
            <GoogleLogo /> {sso.ssoLoading === 'google' ? t('signUp.signingUp') : t('signUp.continueWithGoogle')}
          </button>
          <button
            onClick={() => navigate('/email')}
            className="w-full font-bold rounded-2xl active:scale-[0.98] transition-transform"
            style={{ height: 54, fontSize: 16.5, background: '#46A8F9', color: '#fff', border: 'none', boxShadow: '0 14px 26px -12px rgba(70,168,249,.85)' }}
          >
            {t('welcome.continueEmail', 'Continue with email')}
          </button>
          <p className="text-center text-xs font-semibold" style={{ color: '#8598A9' }}>{APP_VERSION}</p>
        </div>

        <LanguageSelector
          isOpen={showLanguageModal}
          onClose={() => setShowLanguageModal(false)}
        />
        <SsoCodeSheet step={sso.codeStep} busy={sso.codeBusy} error={sso.codeError} onSubmit={sso.submitCode} onCancel={sso.cancelCode} />
      </div>
    </ResponsiveWrapper>
  )
}
